<template>
  <div class="structure" :class="{ 'structure--pro': isProAlgorithm, 'structure--hidden': !visible }">
    <template v-if="isPlaster">
      <Message
        :withToggle="true"
        :text="getPlasterToggleMessage"
        :initial-toggle-state="initialToggleStatePlaster"
        class="message--plaster"
        :id="Structures.plaster"
        @handle="handleToggleEventPlaster($event)"
      />
    </template>
    <slot></slot>
    <div
      class="structure__wrapper"
      :class="{
      'structure__wrapper--facing': isFacing,
      'structure__wrapper--base': isBase,
      'structure__wrapper--plaster': isPlaster,
      'structure__wrapper--sound': isSoundIsolation,
      'structure__wrapper--screed': isScreed,
      'structure__wrapper--sizes': isSizes,
      'structure__wrapper--with-ordered': order,
      'structure__wrapper--layers': withLayers,
      'structure__wrapper--inner-layer': isFacingInnerLayer,
      'structure__wrapper--for-ceiling': isCeiling,
      'structure__wrapper--plaster__hidden': !showPlaster && isPlaster
    }"
    >
      <slot name="subtitle"></slot>
      <template v-for="param of orderedStructure">
        <OptionDropdown
          v-if="param.viewCondition"
          :updateKey="`${sector.id}_${param.id}`"
          :dropdown="param"
          :validationRules="param.validationRules"
          :type="param.type"
          :key="param.id"
          @update="update($event, type, `${param.id}`)"
        ></OptionDropdown>
      </template>
      <template v-if="isSizes || isFacingInnerLayer">
        <Message
          :withToggle="true"
          :text="getToggleMessage"
          :initial-toggle-state="initialToggleState"
          :style="{ 'grid-area': 'toggle' }"
          :id="isSizes ? 'sizes' : 'layers'"
          @handle="handleToggleEvent($event, isSizes, isFacingInnerLayer)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import OptionDropdown from '@/components/dump/OptionDropdown'
import { CalcTypes, ConstructionTypes, Params, Structures } from '@/utils/types'
import DropdownConfigsMixin from '@/components/mixin/DropdownConfigsMixin.vue'
import { mapGetters } from 'vuex'
import Message from '@/components/dump/Message.vue'

export default {
  name: 'Structure',
  components: { Message, OptionDropdown },
  data: () => {
    return {
      Structures: Structures
    }
  },
  props: ['params', 'type', 'sector', 'order', 'visible'],
  mixins: [DropdownConfigsMixin],
  computed: {
    ...mapGetters({
      getConstructionTypes: 'getConstructionTypes',
      sizesGetter: 'sizesGetter',
      facingInnerLayerGetter: 'facingInnerLayerGetter',
      facingLayerGetter: 'facingLayerGetter',
      plasterGetter: 'plasterGetter'
    }),
    initialToggleState() {
      return this.isSizes
        ? this.sector.sizes.sizesBlocked
        : this.sector.facingInnerLayer.facingInnerLayerBlocked
    },
    initialToggleStatePlaster() {
      return this.isPlaster && this.sector.plaster.plasterNotBlocked
    },
    getToggleMessage() {
      const key = this.isSizes ? 'unknown-sizes' : 'layers-are-same'

      return this.$t(`message.constructionParams.common.${key}`)
    },
    getPlasterToggleMessage() {
      return this.$t(`message.constructionParams.common.showPlaster`)
    },
    isFacingInnerLayer() {
      return this.type === Structures.facingInnerLayer
    },
    isSizes() {
      return this.type === Structures.sizes
    },
    isFacing() {
      return this.type === Structures.facing
    },
    isBase() {
      return this.type === Structures.base
    },
    isPlaster() {
      return this.type === Structures.plaster
    },
    plasterNotBlocked() {
      return this.plasterGetter(this.sectorIndex).plasterNotBlocked
    },
    showPlaster() {
      return this.type === Structures.plaster && this.plasterNotBlocked
    },
    isSoundIsolation() {
      return this.type === Structures.soundIsolation
    },
    isCeiling() {
      return this.isSizes && this.currentConstructionType === ConstructionTypes.ceiling
    },
    isScreed() {
      return this.type === Structures.screed
    },
    withLayers() {
      return this.order?.[0] === Params.sheets
    },
    sectorIndex() {
      return this.$store.state.sectors.findIndex((sector) => sector.id === this.sector.id)
    },
    selected() {
      return this.sector[`${this.type}`]
    },
    materials() {
      return this.params?.materials
    },
    material() {
      return this.materials ? this.materials.find((material) => material.title === this.title) : []
    },
    thicknessUnitType() {
      return this.params ? this.params.thicknessUnitType : ''
    },
    densityUnitType() {
      return this.params ? this.params.densityUnitType : ''
    },
    title() {
      return this.sector[`${this.type}`] && this.sector[`${this.type}`][`${this.type}`]
        ? this.sector[`${this.type}`][`${this.type}`].title
        : ''
    },
    formattedMaterials() {
      return this.materials?.map((material) => {
        return {
          id: material.id,
          value: material.title
        }
      }) ?? []
    },
    densities() {
      const unit = this.densityUnitType

      return this.title && (this.isFacing || this.showPlaster || this.isBase || this.isFacingInnerLayer) && this.material?.densities
        ? this.material.densities.map((item, index) => ({ id: index, value: parseInt(item, 10), unit }))
        : [ { id: 0, value: '', unit } ]
    },
    thicknesses() {
      const unit = this.thicknessUnitType

      return this.title && this.material?.thicknesses
        ? this.material?.thicknesses.map((item, index) => {
          return item?.size
            ? { id: index, value: item.size, unit, count: item.count }
            : { id: index, value: item, unit }
        })
        : [ { id: 0, value: '', unit } ]
    },
    sheets() {
      const unit = this.$t('message.units.pc')

      if (this.withLayers) {
        return [1, 2].map((layer, index) => ({ id: index, value: layer, unit }))
      }

      return this.title && this.material?.qtySheet
        ? this.material?.qtySheet.map((item, index) => ({ id: index, value: item, unit }))
        : [ { id: 0, value: '', unit } ]
    },
    isProAlgorithm() {
      return this.sector.algorithm === CalcTypes.pro
    },
    currentConstructionType() {
      return this.sector.constructionType.type
    },
    orderedStructure() {
      return this.order ? this.setStructureOrder() : this.structure
    }
  },
  methods: {
    setStructureOrder() {
      const ordered = []

      this.order.forEach((id, index) => {
        const inputConfig = this.structure.find((input) => input.id === id)
        if (index === 0) {
          inputConfig.disabled = false
        }

        ordered.push(inputConfig)
      })

      return ordered
    },
    update(evt, property, subproperty) {
      const isParamValid = evt?.valid !== undefined ? evt.valid : null

      subproperty === this.type
        ? this.resetConstructionParamsValues(property, evt)
        : this.setConstructionParamsValues(property, subproperty, evt.newValue, isParamValid)
    },
    resetConstructionParamsValues(property, evt) {
      this.structure
        .filter((item) => item.viewCondition)
        .map(item => item.id).forEach((subproperty) => {
          if (this.order && subproperty === this.order[0]) return

          this.setConstructionParamsValues(
            property,
            subproperty,
            subproperty === this.type ? evt.newValue : this.getDefaultValue(subproperty),
            true
          )
        })

      if (this.sector.construction || this.optionalSystems?.length) {
        this.resetConstructions()
      }
    },
    setConstructionParamsValues(property, subproperty, value, validity) {
      const { sectorIndex: index } = this
      this.$store.commit('UPDATE_SECTOR', { index, property, subproperty, value })
      if (property === Structures.facing) {
        this.updateFacingInnerLayer()
      }
      if (validity !== null) {
        const isEditableParamsValidation = this.structure.find((param) => param.id === subproperty)?.editable
        if (isEditableParamsValidation) {
          this.$emit('defineValidity', { index, param: `${property}_${subproperty}`, validity })
        }

        if (!validity) {
          this.resetConstructions()
        }
      }
    },
    updateFacingInnerLayer() {
      const blocked = this.facingInnerLayerGetter(this.sectorIndex)?.facingInnerLayerBlocked ?? false
      if (blocked) {
        const layer = this.facingLayerGetter(this.sectorIndex)

        Object.entries(layer).forEach(([key, value]) => {
          this.$store.commit('UPDATE_SECTOR', {
            index: this.sectorIndex,
            property: Structures.facingInnerLayer,
            subproperty: key === Structures.facing ? Structures.facingInnerLayer : key,
            value
          })
        })
      }
    },
    resetConstructions() {
      ['construction', 'optionalSystems', 'isCalculated']
        .forEach((property) => this.$store.commit('UPDATE_SECTOR', {
          index: this.sectorIndex, property, subproperty: null, value: null
        }))
    },
    getDefaultValue(param) {
      if (!param || !this.material || !this.material?.defaultValues) {
        return null
      }

      return this.material.defaultValues[`${param}`]
    },
    handleToggleEventPlaster(value) {
      const { sectorIndex: index, plasterGetter: getter, type: property } = this
      const blockedSubproperty = 'plasterNotBlocked'

      if (value) {
        this.$store.commit('UPDATE_SECTOR', { index, property, subproperty: blockedSubproperty, value })
      } else {
        Object.keys(getter(this.sectorIndex)).forEach((key) => {
          this.$store.commit('UPDATE_SECTOR', {
            index,
            property: property,
            subproperty: key,
            value: key === blockedSubproperty ? value : null
          })
        })
      }
    },
    handleToggleEvent(value, reset, isFacingInnerLayer) {
      const { sectorIndex: index } = this
      const blockedSubproperty = `${this.type}Blocked`
      const getter = this[`${this.type}Getter`]

      if (value && reset) {
        Object.keys(getter(this.sectorIndex)).forEach(key => {
          this.$store.commit('UPDATE_SECTOR', {
            index,
            property: this.type,
            subproperty: key,
            value: key === blockedSubproperty ? value : null
          })
        })
      } else {
        this.$store.commit('UPDATE_SECTOR', { index, property: this.type, subproperty: blockedSubproperty, value })
      }

      if (isFacingInnerLayer) {
        this.updateFacingInnerLayer()
      }
    }
  }
}
</script>

<style lang="sass">
.wizard
  .structure
    &__wrapper:has(.structure__wrapper--sizes)
      +media((grid-template-columns: (0: repeat(2, 1fr), 1230: minmax(calc(100% / 3), 1fr) 1fr 1fr)))
.structure
  &--hidden
    display: none
  &__layer-title
    margin: rem(15) 0 rem(5)
    font-weight: 400
    line-height: 1.3
  &__wrapper
    display: grid
    grid-gap: rem(16)
    margin: 0 0 rem(20)
    grid-template-rows: auto
    +media((grid-template-columns: (0: repeat(2, 1fr), 1230: 2fr 1fr 1fr)))
    +media((grid-template-areas: (0: 'screed screed' 'thickness thickness', 1230: 'screed screed thickness' 'screed screed thickness')))
    &--base
      +media((grid-template-areas: (0: 'base base' 'density density' 'thickness thickness', 600: 'base base' 'density thickness', 1230: 'base density thickness' 'base density thickness')))
  &--pro
    .structure__wrapper
      grid-gap: rem(16)
      margin: 0 0 rem(20)
      grid-template-rows: auto
      +media((grid-template-columns: (0: repeat(2, 1fr), 1230: repeat(3, 1fr))))
      +media((grid-template-areas: (0: 'isolation isolation' 'thickness thickness', 800: 'isolation thickness' 'isolation thickness', 1230: 'isolation isolation thickness' 'isolation isolation thickness')))
      &--facing
        +media((grid-template-areas: (0: 'facing facing' 'density density' 'thickness thickness' 'sheets sheets', 800: 'facing density' 'thickness sheets', 1230: 'facing facing facing' 'density thickness sheets')))
        margin-bottom: rem(30)
      &--with-ordered
        +media((grid-template-areas: (0: 'sheets sheets' 'facing facing' 'density density' 'thickness thickness', 800: 'sheets facing' 'density thickness', 1230: 'sheets sheets sheets' 'facing density thickness')))
      &--base
        +media((grid-template-columns: (0: repeat(2, 1fr), 1230: repeat(3, minmax(250px, 1fr)))))
        +media((grid-template-areas: (0: 'base base' 'density density' 'thickness thickness', 800: 'base base' 'density thickness', 1230: 'base density thickness' 'base density thickness')))
      &--plaster
        +media((grid-template-columns: (0: repeat(2, 1fr), 1230: repeat(2, minmax(250px, 1fr)))))
        +media((grid-template-areas: (0: 'toggle toggle' 'plaster plaster' 'density density' 'thickness thickness', 800: 'toggle toggle' 'plaster plaster' 'density thickness', 1230: 'toggle toggle' 'plaster density thickness' 'plaster density thickness')))
        &__hidden
          display: none
      &--screed
        +media((grid-template-areas: (0: 'screed screed' 'thickness thickness', 800: 'screed screed' 'thickness thickness', 1230: 'screed screed thickness' 'screed screed thickness')))
      &--sound
        +media((grid-template-areas: (0: 'soundIsolation soundIsolation' 'thickness thickness', 800: 'soundIsolation thickness' 'soundIsolation thickness', 1230: 'soundIsolation soundIsolation thickness' 'soundIsolation soundIsolation thickness')))
      &--sizes
        +media((grid-template-columns: (0: repeat(2, 1fr), 800: repeat(3, 1fr))))
        +media((grid-template-areas: (0: 'x x' 'y y' 'toggle toggle', 800: 'x y .' 'toggle toggle toggle')))
      &--for-ceiling
        +media((grid-template-columns: (0: repeat(2, 1fr), 800: repeat(3, 1fr))))
        +media((grid-template-areas: (0: 'x x' 'y y' 'toggle toggle', 800: 'x y .' 'toggle toggle toggle')))
      &--layers
        +media((grid-template-columns: (0: repeat(2, 1fr), 800: repeat(2, 1fr), 1230: repeat(4, 1fr))))
        +media((grid-template-areas: (0: 'sheets sheets' 'outer outer' 'facing facing' 'density density' 'thickness thickness' 'inner toggle' 'inner_facing inner_facing', 800: 'sheets sheets' 'outer outer' 'facing facing' 'density thickness', 1230: 'sheets sheets sheets sheets' 'outer outer outer outer' 'facing facing facing facing' 'density density thickness thickness')))
      &--inner-layer
        +media((grid-template-columns: (0: repeat(2, 1fr), 800: repeat(2, 1fr), 1230: repeat(4, 1fr))))
        +media((grid-template-areas: (0: 'inner toggle' 'facingInnerLayer facingInnerLayer' 'density density' 'thickness thickness', 800: 'inner toggle ' 'facingInnerLayer facingInnerLayer' 'density thickness', 1230: 'inner toggle toggle toggle' 'facingInnerLayer facingInnerLayer facingInnerLayer facingInnerLayer' 'density density thickness thickness')))
.message
  &--plaster
    grid-area: toggle
    margin-bottom: rem(20)
</style>
